import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Calendar, Loader2, AlertTriangle, TrendingDown, ExternalLink, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ComparisonSharePage — Iter AC Sprint 3 — public read-only comparison report.
 *
 * Route: /share/c/:token
 *
 * Renders a single-column patient-facing view that's the "wow moment" the
 * therapist sends after a session. No login. Token is the auth.
 *
 * Anything the therapist toggled OFF at share time is filtered server-side
 * (we receive the same shape but with that key set to null or removed).
 */
export default function ComparisonSharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/portal/c/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const code = e?.response?.status;
        if (code === 410) setError("This progress report link has expired or been revoked.");
        else if (code === 404) setError("Link not found.");
        else setError("Could not load this report.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <FullCenter>
        <Loader2 className="w-6 h-6 animate-spin text-ink-500" />
      </FullCenter>
    );
  }
  if (error) {
    return (
      <FullCenter>
        <div className="text-center max-w-md" data-testid="share-error">
          <AlertTriangle className="w-6 h-6 mx-auto text-rose-600 mb-2" />
          <p className="text-ink-900">{error}</p>
        </div>
      </FullCenter>
    );
  }
  if (!data) return null;

  const score = data.improvement_score;
  const tone = score?.overall == null ? "neutral" : score.overall >= 60 ? "positive" : score.overall <= 40 ? "negative" : "neutral";
  const toneClass = {
    positive: "from-sage-500 to-sage-600 text-white",
    neutral:  "from-paper-100 to-paper-50 text-ink-900",
    negative: "from-rose-500 to-rose-600 text-white",
  }[tone];

  return (
    <div className="min-h-screen bg-paper-50" data-testid="share-page">
      {/* Branding header */}
      <header className="bg-white border-b border-paper-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            {data.branding?.logo_url && <img src={data.branding.logo_url} alt="" className="w-9 h-9 rounded-full object-cover" />}
            <div>
              <div className="font-heading text-lg text-ink-900">{data.branding?.studio_name || data.branding?.therapist_name || "Posturra"}</div>
              {data.branding?.tagline && <div className="text-xs text-ink-500">{data.branding.tagline}</div>}
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-ink-500">Powered by Posturra</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Title */}
        <div>
          <div className="label-overline text-sage-600 mb-1">Progress report</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium text-ink-900">
            {data.first_name}, here&apos;s how far you&apos;ve come.
          </h1>
          <p className="text-sm text-ink-700 mt-1" data-testid="share-dates">
            {formatDate(data.from_session?.created_at)} → {formatDate(data.to_session?.created_at)}
            {data.days_between != null && <span className="text-ink-500"> · {data.days_between} day{data.days_between === 1 ? "" : "s"} apart</span>}
          </p>
        </div>

        {data.therapist_message && (
          <div className="rounded-2xl bg-sage-50 border border-sage-200 p-4 text-sm text-ink-900" data-testid="share-message">
            &ldquo;{data.therapist_message}&rdquo;
            <div className="text-xs text-ink-500 mt-1.5">— {data.branding?.therapist_name || "Your therapist"}</div>
          </div>
        )}

        {/* Improvement score */}
        {score && (
          <div className={`rounded-3xl p-6 bg-gradient-to-br ${toneClass}`} data-testid="share-score">
            <div className="label-overline opacity-80">Overall progress</div>
            <div className="font-heading text-5xl font-medium mt-1" data-testid="share-score-overall">
              {score.overall == null ? "—" : <>
                {score.overall_signed != null && score.overall_signed >= 0 ? "+" : ""}{score.overall_signed != null ? score.overall_signed : score.overall}<span className="text-2xl opacity-70">%</span>
              </>}
            </div>
            <div className="grid grid-cols-3 gap-4 mt-5">
              <ShareScore label="Pain" pct={score.pain} signed={score.pain_signed} />
              <ShareScore label="Range" pct={score.rom} signed={score.rom_signed} />
              <ShareScore label="Findings" pct={score.findings} signed={score.findings_signed} />
            </div>
          </div>
        )}

        {/* AI narrative */}
        {data.narrative && (
          <section className="bg-white rounded-3xl border border-paper-200 p-5 sm:p-6" data-testid="share-narrative">
            {data.narrative.headline && (
              <p className="text-xl font-medium text-ink-900 leading-snug mb-4" data-testid="share-narrative-headline">
                {data.narrative.headline}
              </p>
            )}
            {data.narrative.improvements?.length > 0 && (
              <SharSection title="Wins so far" items={data.narrative.improvements} tone="sage" testid="share-narrative-improvements" />
            )}
            {data.narrative.still_limited?.length > 0 && (
              <SharSection title="What we&apos;re still working on" items={data.narrative.still_limited} tone="amber" testid="share-narrative-still-limited" />
            )}
            {data.narrative.recommended_next_focus?.length > 0 && (
              <SharSection title="Next focus" items={data.narrative.recommended_next_focus} tone="ink" testid="share-narrative-next" />
            )}
          </section>
        )}

        {/* Photos */}
        {data.photos && data.photos.length > 0 && (
          <section className="bg-white rounded-3xl border border-paper-200 p-5 sm:p-6" data-testid="share-photos">
            <h2 className="font-heading text-xl text-ink-900 mb-4">Before / after</h2>
            <div className="space-y-3">
              {data.photos.map((pair) => (
                <div key={`${pair.kind}-${pair.view}`} className="grid grid-cols-[80px,1fr,1fr] gap-3 items-center" data-testid={`share-photo-${pair.view}`}>
                  <div className="text-xs">
                    <div className="font-medium text-ink-900">{pair.label}</div>
                  </div>
                  <SharePhoto path={pair.a_path} token={token} />
                  <SharePhoto path={pair.b_path} token={token} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Pain card */}
        {data.pain?.delta_text && (
          <section className="bg-white rounded-3xl border border-paper-200 p-5 sm:p-6" data-testid="share-pain">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h2 className="font-heading text-xl text-ink-900">Pain</h2>
              <TrendingDown className={`w-5 h-5 ${data.pain.score > 0 ? "text-sage-700" : "text-ink-500"}`} />
            </div>
            <div className="text-sm">
              <div className="text-ink-700">
                From <span className="font-medium text-ink-900">{data.pain.a_value ?? "—"}/10</span>
                {" → "}
                To <span className="font-medium text-ink-900">{data.pain.b_value ?? "—"}/10</span>
              </div>
              <div className="text-xs text-ink-500 mt-1">{data.pain.delta_text}{data.pain.delta_pct != null && ` · ${data.pain.delta_pct > 0 ? "+" : ""}${data.pain.delta_pct}%`}</div>
            </div>
          </section>
        )}

        {/* Booking CTA */}
        {data.branding?.booking_link && (
          <div className="text-center" data-testid="share-booking">
            <a
              href={data.branding.booking_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 min-h-[52px] px-7 rounded-full bg-ink-900 hover:bg-ink-700 text-white text-base font-medium transition-colors"
              data-testid="share-booking-btn"
            >
              <Calendar className="w-4 h-4" /> Book your next session
              <ExternalLink className="w-3.5 h-3.5 opacity-70" />
            </a>
          </div>
        )}

        <footer className="text-center text-xs text-ink-500 pb-12 pt-4">
          <div className="inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" />
            Generated by Posturra · This link expires {data.meta?.expires_at && new Date(data.meta.expires_at).toLocaleDateString()}
          </div>
        </footer>
      </main>
    </div>
  );
}

function FullCenter({ children }) {
  return <div className="min-h-screen bg-paper-50 flex items-center justify-center p-6">{children}</div>;
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

function ShareScore({ label, pct, signed }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="font-heading text-xl font-medium mt-0.5">
        {pct == null ? "—" : <>{signed != null && signed >= 0 ? "+" : ""}{signed != null ? signed : pct}<span className="text-sm opacity-70">%</span></>}
      </div>
    </div>
  );
}

function SharSection({ title, items, tone, testid }) {
  const toneClass = {
    sage:  "bg-sage-50/60 border-sage-200",
    amber: "bg-amber-50/60 border-amber-200",
    ink:   "bg-paper-50 border-paper-200",
  }[tone];
  return (
    <div className={`rounded-2xl border ${toneClass} p-4 mb-3 last:mb-0`} data-testid={testid}>
      <div className="label-overline mb-2">{title}</div>
      <ul className="space-y-1 list-disc list-inside text-sm text-ink-900 leading-relaxed">
        {items.map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    </div>
  );
}

function SharePhoto({ path, token }) {
  if (!path) return <div className="bg-paper-50 border border-dashed border-paper-300 rounded-xl h-32 flex items-center justify-center text-xs text-ink-500">No photo</div>;
  const url = `${API}/portal/c/${token}/photo?path=${encodeURIComponent(path)}`;
  return <img src={url} alt="" className="w-full h-32 object-cover rounded-xl bg-paper-100" />;
}
